import { pollingUtil } from '@tt4b/ttam-utils';
import { postAsyncSubmit, getAsyncSubmitResult, IAsyncSubmitData } from '@tt4b/ttam-hooks';
import { Message } from 'byted-vui';
import { getFormatSubmitResult } from './parse-submit-error';
import { IAsyncSubmitResult } from '../types';
import { MockProgress } from './mock-progress';
import {
  trackEventAsyncSubmit,
  trackEventAsyncSubmitDuration,
  trackEventAsyncSubmitAdgroupAndAdCreateSource,
} from './submit-track';
import { isObject } from 'lodash-es';

export const SUBMIT_STATUS = {
  SUBMITTING: 0, // 提交中
  SUCCESS: 1, // 全部成功
  FAIL: 2, // 全部失败
  SOME_FAIL: 3, // 部分失败，部分成功
  API_FAIL: -1, // 接口报错异常，非 200
};

/**
 * 对异步三合一接口的结果进行格式化处理，返回符合 UI 的数据结构
 */
const _formatResult = (result: Partial<IAsyncSubmitResult>) => {
  // 解析报错信息
  const formatSubmitResult = getFormatSubmitResult(result);

  const { campaignId, successResult } = formatSubmitResult;

  const adgroupIds: string[] = [];
  const creativeIdsObj: Record<string, string[]> = {};
  const successAdgroupList: {
    adgroupIndex: string;
    adgroupId: string;
    adgroupSnapId?: string;
  }[] = [];
  Object.keys(successResult).forEach(adgIndex => {
    const currentResult = successResult[adgIndex];
    const { adId, isAco, creativeIdsObj: originCreativeIdsObj, adgroupSnapId = '' } = currentResult;
    adgroupIds.push(adId);
    successAdgroupList.push({
      adgroupIndex: adgIndex,
      adgroupId: adId,
      adgroupSnapId,
    });
    Object.keys(originCreativeIdsObj).forEach(creativeUuid => {
      if (isAco) {
        return;
      }
      if (!creativeIdsObj[adId]) {
        creativeIdsObj[adId] = [];
      }
      const cid = originCreativeIdsObj[creativeUuid]?.creativeId || '';
      creativeIdsObj[adId] = creativeIdsObj[adId].concat(cid);
    });
  });

  return {
    campaignId,
    adgroupIds,
    creativeIdsObj,
    status: result.status as number,
    isSuccess: result.status === SUBMIT_STATUS.SUCCESS,
    // 完整的提交结果
    formatSubmitResult,
    originResult: result,
    // 维护一个成功的index和adid的列表, name template使用
    successAdgroupList,
    isExistSuccess: undefined,
  };
};

/**
 * 轮询异步创建结果
 **/
const handlePollingResult = ({ jobId, onProgress, isSnapData = false }) => {
  const progressInstance = new MockProgress({
    onProgress(val) {
      onProgress?.(val);
    },
  });

  // 这个轮询工具封装的重试方法有问题，无法直接用，因此在 failCheck 来做验证
  let tried = 0;
  const maxTry = 3; // 重试 3 次，如果还是失败，则认为异步创建失败

  const config = {
    gap: 1000,
    task: () =>
      getAsyncSubmitResult(jobId, isSnapData).catch(resp => {
        const errorData = resp?.response?.data || {};
        const extraErrorInfo = errorData.extra || { message: errorData?.msg || '' };
        return { ...extraErrorInfo, status: SUBMIT_STATUS.API_FAIL };
      }),
    progress: result => {
      if (result?.progress) {
        const { total_value: totalValue, current_value: currentValue, batch_size: batchSize } = result?.progress || {};

        const batchNum = Math.ceil(totalValue / batchSize);
        const maxProgress = Math.ceil((currentValue / totalValue) * 100) + Math.ceil(100 / batchNum);
        progressInstance.set(maxProgress, 5000);
      }
    },
    successCheck: res => res?.status === SUBMIT_STATUS.SUCCESS || res?.status === SUBMIT_STATUS.FAIL,
    // 接口失败单独处理
    failCheck: res => res?.status === SUBMIT_STATUS.API_FAIL && ++tried >= maxTry,
  };

  return pollingUtil(config).then(res => {
    progressInstance.done();
    return res as Record<string, unknown>;
  });
};

/**
 * 进行异步提交
 * @param data
 * @param config
 */
export const handleAsyncSubmit = async (data: IAsyncSubmitData, { onProgress, isSnapData = false }) => {
  try {
    const startTime = Date.now();
    const { async_request_id: jobId } = await postAsyncSubmit(data, isSnapData).catch(resp => {
      const errorData = resp?.response?.data || {};
      const msg = errorData?.extra?.message || errorData?.msg || '';
      trackEventAsyncSubmit({ type: 'async_creation_create_api_fail' });
      return Promise.reject(msg);
    });
    trackEventAsyncSubmitDuration('async_creation_create', Date.now() - startTime);

    const result = await handlePollingResult({ jobId, onProgress, isSnapData }).catch(error => {
      const msg = error?.resp?.message || error?.resp?.msg || '';
      trackEventAsyncSubmit({ type: 'async_creation_detail_api_fail' });
      return Promise.reject(msg);
    });
    trackEventAsyncSubmitDuration('async_creation_detail', Date.now() - startTime);
    if (isObject(result?.result)) {
      trackEventAsyncSubmitAdgroupAndAdCreateSource({ ...result.result });
    }
    return _formatResult(result);
  } catch (error) {
    Message.error(error || 'Request Error.');
    return _formatResult({ status: SUBMIT_STATUS.API_FAIL });
  }
};
